import {ref, Ref, toRaw, isProxy} from 'vue';
import {useLoginInfoStore} from '@/stores/loginInfo';
import {
  ApprovalPayload,
  BroadcastMessage,
  ChatMessagePayload,
  ChatUnreadCountPayload,
  CountUpdatePayload,
  MessageMetadata,
  NotificationPayload,
  PresenceUpdate,
  TaskPayload
} from '@/utils/presenters/interfaces';
import {BroadcastMessageType} from "@/utils/UIConstants/enums.ts";

type MessageHandler<T = any> = (payload: T, message: BroadcastMessage<T>) => void;

/**
 * Singleton BroadcastChannel service for cross-tab communication
 * Replaces polling with real-time push notifications across browser tabs
 */
class NamaBroadcastChannelService {
  private static instance: NamaBroadcastChannelService;
  private channel: BroadcastChannel | null = null;
  private handlers: Map<BroadcastMessageType, Set<MessageHandler>> = new Map();
  private tabId: string;
  private isLeader: Ref<boolean> = ref(false);
  private leaderCheckInterval: number | null = null;
  private lastLeaderPing: number = 0;

  private constructor() {
    this.tabId = `tab-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  static getInstance(): NamaBroadcastChannelService {
    if (!NamaBroadcastChannelService.instance) {
      NamaBroadcastChannelService.instance = new NamaBroadcastChannelService();
    }
    return NamaBroadcastChannelService.instance;
  }

  /**
   * Initialize the broadcast channel
   * Call this on app startup after login
   */
  init(): void {
    if (this.channel) {
      return; // Already initialized
    }
    if (typeof BroadcastChannel === 'undefined') {
      console.warn('BroadcastChannel not supported in this browser');
      return;
    }
    this.channel = new BroadcastChannel('nama-erp-sync');
    this.channel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
      this.handleMessage(event.data);
    };
    // Start leader election
    this.startLeaderElection();
  }

  /**
   * Leader election - only one tab does server polling
   * Other tabs receive updates via broadcast
   */
  private startLeaderElection(): void {
    // Try to become leader immediately
    this.tryBecomeLeader();
    // Check leader status periodically
    this.leaderCheckInterval = window.setInterval(() => {
      const now = Date.now();
      // If no leader ping in 10 seconds, try to become leader
      if (now - this.lastLeaderPing > 10000) {
        this.tryBecomeLeader();
      }
    }, 5000);
    // Listen for leader pings
    this.on(BroadcastMessageType.TAB_PING, (_, msg) => {
      this.lastLeaderPing = Date.now();
      if (msg.senderId !== this.tabId) {
        // Another tab is leader
        this.isLeader.value = false;
      }
    });
  }

  private tryBecomeLeader(): void {
    this.isLeader.value = true;
    this.broadcast(BroadcastMessageType.TAB_BECAME_LEADER, {tabId: this.tabId});
    this.startLeaderPing();
  }

  private startLeaderPing(): void {
    if (!this.isLeader.value) return;

    // Send ping every 5 seconds while leader
    const ping = () => {
      if (this.isLeader.value) {
        this.broadcast(BroadcastMessageType.TAB_PING, {tabId: this.tabId});
        setTimeout(ping, 5000);
      }
    };
    ping();
  }

  /**
   * Check if this tab is the leader (responsible for server polling)
   */
  isLeaderTab(): boolean {
    return this.isLeader.value;
  }

  /**
   * Get reactive leader status
   */
  getLeaderStatus(): Ref<boolean> {
    return this.isLeader;
  }

  /**
   * Broadcast a message to all other tabs
   */
  broadcast<T>(type: BroadcastMessageType, payload: T): void {
    if (!this.channel) {
      return;
    }
    const message: BroadcastMessage<T> = {
      type,
      payload: this.deepToRaw(payload),
      senderId: this.tabId,
      timestamp: Date.now()
    };
    this.channel.postMessage(message);
  }

  /**
   * Recursively unwrap Vue reactive proxies so the object can be
   * cloned by the structured-clone algorithm used by postMessage.
   */
  private deepToRaw<T>(obj: T): T {
    const raw: any = isProxy(obj) ? toRaw(obj) : obj;
    if (raw === null || raw === undefined) return raw;
    if (Array.isArray(raw)) {
      return raw.map((item: any) => this.deepToRaw(item)) as T;
    }
    if (typeof raw === 'object' && raw.constructor === Object) {
      const result: any = {};
      for (const key of Object.keys(raw)) {
        result[key] = this.deepToRaw(raw[key]);
      }
      return result;
    }
    return raw;
  }

  /**
   * Register a handler for a specific message type
   */
  on<T>(type: BroadcastMessageType, handler: MessageHandler<T>): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(handler as MessageHandler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(type)?.delete(handler as MessageHandler);
    };
  }

  /**
   * Remove a handler
   */
  off<T>(type: BroadcastMessageType, handler: MessageHandler<T>): void {
    this.handlers.get(type)?.delete(handler as MessageHandler);
  }

  private handleMessage(message: BroadcastMessage): void {
    // Ignore messages from self
    if (message.senderId === this.tabId) {
      return;
    }

    const handlers = this.handlers.get(message.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message.payload, message);
        } catch (error) {
          console.error(`[BroadcastChannel] Error in handler for ${message.type}:`, error);
        }
      });
    }
  }

  // ============ Convenience methods for common operations ============
  /**
   * Broadcast a new chat message to other tabs
   */
  broadcastChatMessage(message: MessageMetadata, conversationId?: string): void {
    this.broadcast<ChatMessagePayload>(BroadcastMessageType.CHAT_NEW_MESSAGE, {
      message,
      conversationId: conversationId || message.conversationId
    });
  }

  /**
   * Broadcast chat unread count update
   */
  broadcastChatUnreadCount(conversationId: string, count: number, totalUnread: number): void {
    this.broadcast<ChatUnreadCountPayload>(BroadcastMessageType.CHAT_UNREAD_COUNT, {
      conversationId,
      count,
      totalUnread
    });
    // Also update the store directly for this tab
    useLoginInfoStore().updateChatMessages(totalUnread);
  }

  /**
   * Broadcast user presence update
   */
  broadcastPresence(update: PresenceUpdate): void {
    this.broadcast(BroadcastMessageType.CHAT_PRESENCE, update);
  }

  /**
   * Broadcast notification updates (called by leader tab after server poll)
   */
  broadcastNotifications(notifications: namaTypes.DTOTabularResult[], count: number): void {
    this.broadcast<NotificationPayload>(BroadcastMessageType.NOTIFICATION_NEW, {
      notifications,
      count
    });
  }

  /**
   * Broadcast notification count update
   */
  broadcastNotificationCount(count: number): void {
    this.broadcast<CountUpdatePayload>(BroadcastMessageType.NOTIFICATION_COUNT_UPDATE, {count});
  }

  /**
   * Broadcast approval updates (called by leader tab after server poll)
   */
  broadcastApprovals(approvals: namaTypes.DTOTabularResult[], count: number): void {
    this.broadcast<ApprovalPayload>(BroadcastMessageType.APPROVAL_NEW, {
      approvals,
      count
    });
  }

  /**
   * Broadcast approval count update
   */
  broadcastApprovalCount(count: number): void {
    this.broadcast<CountUpdatePayload>(BroadcastMessageType.APPROVAL_COUNT_UPDATE, {count});
  }

  /**
   * Broadcast task updates
   */
  broadcastTasks(tasks: namaTypes.GUIActionResult[], count: number): void {
    this.broadcast<TaskPayload>(BroadcastMessageType.TASK_UPDATE, {
      tasks,
      count
    });
  }

  /**
   * Setup default handlers to update Pinia stores
   * Call this after init() to automatically sync stores across tabs
   */
  setupStoreSync(): void {
    const loginInfoStore = useLoginInfoStore();

    // Sync notifications
    this.on<NotificationPayload>(BroadcastMessageType.NOTIFICATION_NEW, (payload) => {
      loginInfoStore.updateNotifications(payload.notifications, payload.count);
    });

    this.on<CountUpdatePayload>(BroadcastMessageType.NOTIFICATION_COUNT_UPDATE, (payload) => {
      loginInfoStore.notificationsCount = payload.count;
    });

    // Sync approvals
    this.on<ApprovalPayload>(BroadcastMessageType.APPROVAL_NEW, (payload) => {
      loginInfoStore.updateApprovals(payload.approvals, payload.count);
    });

    this.on<CountUpdatePayload>(BroadcastMessageType.APPROVAL_COUNT_UPDATE, (payload) => {
      loginInfoStore.approvalsCount = payload.count;
    });

    // Sync tasks
    this.on<TaskPayload>(BroadcastMessageType.TASK_UPDATE, (payload) => {
      loginInfoStore.updateTasks(payload.tasks, payload.count);
    });

    // Sync chat unread count
    this.on<ChatUnreadCountPayload>(BroadcastMessageType.CHAT_UNREAD_COUNT, (payload) => {
      loginInfoStore.updateChatMessages(payload.totalUnread);
    });

  }

  /**
   * Cleanup - call on logout or app destroy
   */
  destroy(): void {
    if (this.leaderCheckInterval) {
      clearInterval(this.leaderCheckInterval);
      this.leaderCheckInterval = null;
    }
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.handlers.clear();
    this.isLeader.value = false;
  }
}

// Export singleton instance
export const namaBroadcast = NamaBroadcastChannelService.getInstance();

// Export for direct use
export default NamaBroadcastChannelService;
